import React, { ReactNode, useRef, useState, useEffect } from "react";
import Slider, { Settings } from "react-slick";
import { EWsrvTransform, IMediaItem } from "../../lib/Media/util";
import { ILinkFunc } from "../../lib/util";
import { Button } from "../Button/Button";
import {
  BsArrowUpCircle,
  BsArrowDownCircle,
  BsArrowRightCircle,
  BsArrowLeftCircle,
} from "react-icons/bs";
import "./featuredWithCarouselContent.css";
import truncate from "truncate-html";
import Heading from "./Heading";
import { WsrvImage } from "../Media";

export enum ESlidesMode {
  vertical = "vertical",
  horizontal = "horizontal",
}

export interface ISlideContent {
  title: string;
  subtitle?: string;
  content: string;
  link?: { label: string; url: string };
  image: IMediaItem;
}

export interface IFeaturedWithCarouselContentProps {
  containerClassName?: string;
  className?: string;
  linkFunc?: ILinkFunc;
  slides: ISlideContent[];
  activeSlideIndicator?: ReactNode;
  prevSlideButtonContent?: ReactNode;
  nextSlideButtonContent?: ReactNode;
  slickSettings?: Partial<Settings>;
  slideMode?: ESlidesMode;
  showSlidePage?: boolean;
  children?: ReactNode;
  title?: string;
  secondaryTitle?: string;
  secondaryTitleClassName?: string;
  titleClassName?: string;
  titleHeadingLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
}

export const FeaturedWithCarouselContent: React.FC<IFeaturedWithCarouselContentProps> =
  ({
    containerClassName = "",
    className = "",
    slides,
    linkFunc,
    activeSlideIndicator = undefined,
    prevSlideButtonContent = undefined,
    nextSlideButtonContent = undefined,
    slideMode = ESlidesMode.vertical,
    slickSettings,
    showSlidePage = true,
    children,
    title,
    titleClassName,
    secondaryTitle,
    secondaryTitleClassName,
    titleHeadingLevel = "h1",
  }) => {
    const slider = useRef<any>(null);
    const linkf = linkFunc || ((props) => <a {...props} />);

    const [activeSlide, setActiveSlide] = useState(-1);
    const onSlideClickHandler = (slideIndex: any) => {
      setActiveSlide(slideIndex);
      slider.current?.slickGoTo(slideIndex);
    };
    const onPrevButtonClickHandler = () => {
      slider.current?.slickPrev();
    };
    const onNextButtonClickHandler = () => {
      slider.current?.slickNext();
    };

    type SlideLayout = {
      nonActiveSlideClass: string;
      activeSlideClass: string;
      slideContentWrapperClass: string;
      slideTitleClass: string;
      slidePagerClass: string;
      slideMainContentClass: string;
      slideButtonClass: string;
      slideIndicatorClass: string;
      nonActiveSlideTitleClass: string;
      activeSlideTitleClass: string;
    };
    const getSlideModeLayoutClasses = (index: number): SlideLayout => {
      let slidesLayoutClasses: SlideLayout = {
        nonActiveSlideClass: "",
        activeSlideClass: "",
        slideContentWrapperClass: "",
        slideTitleClass: "",
        slidePagerClass: "",
        slideMainContentClass: "",
        slideButtonClass: "",
        slideIndicatorClass: "",
        nonActiveSlideTitleClass: "",
        activeSlideTitleClass: "",
      };
      switch (slideMode) {
        case ESlidesMode.horizontal:
          slidesLayoutClasses.slideContentWrapperClass = `relative w-full h-full`;
          slidesLayoutClasses.nonActiveSlideClass = "w-full h-full";
          slidesLayoutClasses.activeSlideClass = "w-full h-full";
          break;
        default:
          slidesLayoutClasses.slideContentWrapperClass = `relative w-full h-full lg:border-b py-4 flex flex-row flex-wrap justify-between ${activeSlide == index ? "lg:py-14" : "lg:py-10"
            }`;
          slidesLayoutClasses.slideTitleClass =
            "inline-block py-0 lg:max-w-xs order-1 lg:order-0";
          slidesLayoutClasses.slidePagerClass = `inline-block font-extrabold text-base order-0 lg:order-1 w-full lg:w-auto ${activeSlide == index ? "" : "lg:hidden"
            }`;
          slidesLayoutClasses.slideMainContentClass = `order-2 py-4 lg:pt-8 lg:pb-10 w-full text-base leading-7 tracking-widest  ${activeSlide == index ? "" : "lg:hidden"
            }`;
          slidesLayoutClasses.slideButtonClass = `order-3 normal-case italic font-normal ${activeSlide == index ? "" : "lg:hidden"
            }`;
          slidesLayoutClasses.slideIndicatorClass = `active-slide-indicator absolute -right-36 hidden  ${activeSlide == index ? "lg:block" : ""
            }`;

          slidesLayoutClasses.nonActiveSlideClass =
            "featured-w-carousel-slide w-full bg-body-bg-inverted text-body-text-inverted px-2 lg:px-16 h-auto lg:h-[110px] cursor-pointer";
          slidesLayoutClasses.activeSlideClass =
            "featured-w-carousel-slide w-full px-2 lg:pl-16 lg:pr-32 h-auto lg:h-[440px] bg-body-bg-inverted lg:bg-body-bg text-body-text-inverted lg:text-body-text";
          slidesLayoutClasses.nonActiveSlideTitleClass =
            "uppercase font-normal lg:font-black text-3xl lg:text-xl text-body-text-inverted";
          slidesLayoutClasses.activeSlideTitleClass =
            "uppercase font-normal text-3xl text-body-text-inverted lg:text-body-text";
          break;
      }

      return slidesLayoutClasses;
    };

    const slideElements = slides.map((slide, index) => {
      const {
        activeSlideClass,
        nonActiveSlideClass,
        slideContentWrapperClass,
        slideTitleClass,
        activeSlideTitleClass,
        nonActiveSlideTitleClass,
        slidePagerClass,
        slideMainContentClass,
        slideButtonClass,
        slideIndicatorClass,
      } = getSlideModeLayoutClasses(index);
      return (
        <div
          key={index}
          className={`content-slide-wrapper ${activeSlide == index ? activeSlideClass : nonActiveSlideClass
            }`}
          onClick={() => onSlideClickHandler(index)}
        >
          <div className={`content-slide ${slideContentWrapperClass}`}>
            <h3
              className={`${slideTitleClass} ${activeSlide == index
                ? activeSlideTitleClass
                : nonActiveSlideTitleClass
                }`}
            >
              {slide.title}
            </h3>
            {slide.subtitle && <h4>{slide.subtitle}</h4>}
            {showSlidePage && (
              <span className={`${slidePagerClass}`}>
                {index + 1}/{slides.length}
              </span>
            )}
            <div
              className={`${slideMainContentClass}`}
              dangerouslySetInnerHTML={{
                __html: truncate(slide.content, 212, { keepWhitespaces: true }),
              }}
            ></div>
            {slide.link && (
              <Button
                line={true}
                icon={false}
                linkProps={{
                  href: slide.link.url,
                  className: `${slideButtonClass}`,
                }}
                linkFunc={linkf}
              >
                {slide.link.label}
              </Button>
            )}
            {slideMode == ESlidesMode.vertical && (
              <div className={`${slideIndicatorClass}`}>
                {activeSlideIndicator && activeSlideIndicator}
                {!activeSlideIndicator && (
                  <BsArrowRightCircle className="text-5xl"></BsArrowRightCircle>
                )}
              </div>
            )}
          </div>
        </div>
      );
    });

    let imageColClassNames = "";
    let contentColClassNames = "";
    let prevButtonClassNames = "";
    let nextButtonClassNames = "";

    if (slideMode == ESlidesMode.horizontal) {
      contentColClassNames =
        "w-full md:w-1/3 self-stretch order-2 flex flex-col justify-center";
      imageColClassNames = "w-full md:w-2/3 order-1 self-stretch";
      prevButtonClassNames = "absolute left-0 ml-4";
      nextButtonClassNames = "absolute right-1/3 mr-4";
    } else {
      contentColClassNames =
        "relative z-10 w-full order-2 lg:order-1 flex flex-col items-center lg:items-start";
      imageColClassNames =
        "lg:absolute lg:right-0 w-full lg:w-1/2 xl:w-7/12 order-1 lg:order-2 h-[440px] md:h-[660px] lg:h-[880px]";
      prevButtonClassNames =
        "grow lg:grow-0 bg-body-bg-inverted lg:bg-body-bg lg:pl-16 hidden lg:flex flex-row justify-center lg:justify-start items-center";
      nextButtonClassNames =
        " grow lg:grow-0 bg-body-bg-inverted lg:bg-body-bg lg:pl-16 hidden lg:flex justify-center lg:justify-start items-center";
    }

    const [initSlider, setInitSlider] = useState(false);
    useEffect(() => {
      let timer = setTimeout(() => {
        setActiveSlide(0);
      }, 750);
      return () => {
        clearTimeout(timer);
      };
    }, [initSlider]);

    useEffect(() => {
      function resizeCarousel(event: any) {
        setActiveSlide(-1);
        setTimeout(() => {
          setActiveSlide(0);
          slider.current.slickGoTo(0);
        }, 750);
      }
      window.addEventListener("resize", resizeCarousel);
      return () => {
        window.removeEventListener("resize", resizeCarousel);
      };
    }, []);

    return (
      <section
        className={`featured-w-carousel-content ${containerClassName} relative w-full ${slideMode == ESlidesMode.horizontal
          ? "flex flex-col"
          : "featured-w-carousel-container"
          }`}
      >
        {title && (
          <Heading className={titleClassName} headingLevel={titleHeadingLevel}>
            {title}
            {secondaryTitle && (
              <span className={secondaryTitleClassName}>{secondaryTitle}</span>
            )}
          </Heading>
        )}
        <div
          className={`${className} relative mx-auto w-full flex flex-row flex-wrap items-center ${slideMode == ESlidesMode.horizontal ? "grow" : "featured-w-carousel"
            }`}
        >
          <div className={`first-col ${contentColClassNames}`}>
            <button
              className={`prev-button ${prevButtonClassNames}`}
              onClick={onPrevButtonClickHandler}
            >
              {prevSlideButtonContent && prevSlideButtonContent}
              {!prevSlideButtonContent && (
                <>
                  {slideMode == ESlidesMode.vertical && (
                    <BsArrowUpCircle className="-rotate-90 lg:rotate-0 text-body-text-inverted lg:text-body-text text-xl lg:text-3xl" />
                  )}
                  {slideMode == ESlidesMode.horizontal && (
                    <BsArrowLeftCircle className="text-composite-text text-xl lg:text-5xl" />
                  )}
                </>
              )}
            </button>
            <Slider
              ref={slider}
              vertical={slideMode == ESlidesMode.vertical}
              slidesToShow={slideMode == ESlidesMode.vertical ? 5 : 1}
              slidesToScroll={1}
              dots={false}
              arrows={false}
              verticalSwiping={slideMode == ESlidesMode.vertical}
              infinite={true}
              className={`h-auto w-full max-w-full ${slideMode == ESlidesMode.vertical ? "lg:h-[880px]" : ""
                }`}
              onInit={() => {
                setInitSlider(!initSlider);
              }}
              afterChange={
                slideMode == ESlidesMode.vertical
                  ? () => { }
                  : (index) => setActiveSlide(index)
              }
              responsive={[
                {
                  breakpoint: 1023,
                  settings: {
                    vertical: false,
                    verticalSwiping: false,
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    afterChange: (index) => setActiveSlide(index),
                    dots: slideMode == ESlidesMode.vertical,
                  },
                },
              ]}
              {...slickSettings}
            >
              {slideElements}
            </Slider>
            <button
              className={`next-button ${nextButtonClassNames}`}
              onClick={onNextButtonClickHandler}
            >
              {nextSlideButtonContent && nextSlideButtonContent}
              {!nextSlideButtonContent && (
                <>
                  {slideMode == ESlidesMode.vertical && (
                    <BsArrowDownCircle className="-rotate-90 lg:rotate-0 text-body-text-inverted lg:text-body-text text-xl lg:text-3xl" />
                  )}
                  {slideMode == ESlidesMode.horizontal && (
                    <BsArrowRightCircle className="text-composite-text text-xl lg:text-5xl" />
                  )}
                </>
              )}
            </button>
          </div>
          <div className={`second-col ${imageColClassNames}`}>
            <WsrvImage
              urlParams={{
                originURL: slides[activeSlide]?.image.url,
                preset: EWsrvTransform.cover16x9
              }}
              imgProps={{
                className: "w-full min-h-full object-cover object-center",
                wrapperClassName: "w-full min-h-full h-full",
                wrapperProps: {
                  style: {
                    display: "block"
                  }
                }
              }}
            />
          </div>
        </div>
        {children}
      </section>
    );
  };

export default FeaturedWithCarouselContent;
